// layout/Navigation/TopNav.tsx
import React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import type { SxProps, Theme } from '@mui/material';
import { AppBar, Box, Container, IconButton, Toolbar, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { useChameleon } from '@/contexts/ChameleonContext';
import { permissionService } from '@/utils/permissionService';
import { ChameleonPicker } from './ChameleonPicker';
import { DemoToggle } from './DemoToggle';
import { ThemeToggle } from './ThemeToggle';

// Import icons
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import SettingsApplicationsOutlinedIcon from '@mui/icons-material/SettingsApplicationsOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { alpha, colors } from '@/styles/base/tokens';

interface NavItem {
    path: string;
    icon: SvgIconComponent;
    label: string;
    tooltip: string;
    visible: boolean;
}

// Logo for the bar's background (tokens.ts appBarLogo): full colour on white, reverse on dark
const transparentLogo = colors.appBarLogo;

const DEFAULT_HELP_EMAIL = 'help@infinitum8.com';

export function TopNav() {
    const { logout, authState } = useAuth();
    const { user } = authState;
    const { chameleon } = useChameleon();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleHelpClick = () => {
        // Deliberately authState.user, not the chameleon-effective user — the person
        // needing help is the impersonator, and their DOR's address travels in the session.
        const helpEmail = user?.help_email ?? DEFAULT_HELP_EMAIL;
        const subject = encodeURIComponent('Help Request');
        const body = encodeURIComponent('Hi Helpdesk, I need assistance with...');
        window.location.href = `mailto:${helpEmail}?subject=${subject}&body=${body}`;
    };

    // Navigation configuration including icons and labels
    const navItems: NavItem[] = [
        {
            path: '/overview',
            icon: HomeOutlinedIcon,
            label: 'Overview',
            tooltip: 'Overview dashboard',
            visible: true,
        },
        {
            path: '/dashboard',
            icon: InsightsOutlinedIcon,
            label: 'Dashboard',
            tooltip: 'Advanced analytics',
            visible: true,
        },
        {
            path: '/campaigns',
            // A clipboard: what is put in place before anything is measured. The megaphone
            // stays with the Campaigns tab, where it means campaign.
            icon: AssignmentOutlinedIcon,
            label: 'Setup',
            tooltip: 'Campaigns, creatives, bookings and site tags',
            visible: true,
        },
        {
            path: '/admin',
            icon: AdminPanelSettingsOutlinedIcon,
            label: 'Admin',
            tooltip: 'Administrative tools',
            visible: true,
        },
        {
            path: '/system-admin',
            icon: SettingsApplicationsOutlinedIcon,
            label: 'System',
            tooltip: 'System administration',
            visible: permissionService.canAccessSuperAdmin(user) && !chameleon.active,
        },
    ];

    // If chameleon activates while on system-admin, redirect to dashboard
    React.useEffect(() => {
        if (chameleon.active && location.pathname === '/system-admin') {
            navigate('/dashboard', { replace: true });
        }
    }, [chameleon.active, location.pathname, navigate]);

    // Determine active route based on current path
    const isActive = (path: string): boolean => location.pathname === path;

    // Base styling for nav items - modern and subtle
    const getNavButtonStyle = (active: boolean): SxProps<Theme> => ({
        color: colors.appBarText,
        opacity: active ? 1 : 0.8,
        position: 'relative',
        mx: { xs: 0.5, sm: 1 },
        '&:hover': {
            backgroundColor: alpha(colors.appBarText, 0.08),
            opacity: 1,
        },
        '&::after': active
            ? {
                  content: '""',
                  position: 'absolute',
                  bottom: 0,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '30%',
                  height: '2px',
                  backgroundColor: colors.appBarText,
              }
            : {},
    });

    return (
        <AppBar
            position="static"
            sx={{
                bgcolor: 'appBar.main',
                color: colors.appBarText,
                boxShadow: 'none',
                // A white bar needs a line where it meets a white page
                borderBottom: `1px solid ${colors.border}`,
                py: { xs: 1, md: 1.5 },
                flexShrink: 0,
            }}
        >
            <Container maxWidth={false} sx={{ px: { xs: 1, sm: 2, md: 3 } }}>
                <Toolbar
                    disableGutters
                    sx={{
                        minHeight: { xs: '48px', md: '56px' },
                        py: 0,
                        justifyContent: 'space-between',
                    }}
                >
                    {/* Logo */}
                    <Box
                        component="img"
                        src={transparentLogo}
                        alt="Infinitum"
                        sx={{
                            height: 'auto',
                            width: { xs: 180, sm: 220, md: 260 },
                            mr: 2,
                        }}
                    />

                    {/* Navigation Links - Responsive design */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexGrow: 1,
                            justifyContent: { xs: 'center', md: 'flex-start' },
                        }}
                    >
                        {navItems
                            .filter((item) => item.visible)
                            .map((item) => {
                                const Icon = item.icon;
                                const active = isActive(item.path);

                                return (
                                    <Tooltip key={item.path} title={item.tooltip} arrow placement="bottom">
                                        <IconButton
                                            component={NavLink}
                                            to={item.path}
                                            disableRipple
                                            size="medium"
                                            sx={getNavButtonStyle(active)}
                                        >
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    py: 0.5,
                                                }}
                                            >
                                                <Icon fontSize={isMobile ? 'small' : 'medium'} />
                                                {!isMobile && (
                                                    <Box
                                                        component="span"
                                                        sx={{
                                                            fontSize: '0.65rem',
                                                            mt: 0.5,
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            fontWeight: active ? 500 : 400,
                                                        }}
                                                    >
                                                        {item.label}
                                                    </Box>
                                                )}
                                            </Box>
                                        </IconButton>
                                    </Tooltip>
                                );
                            })}
                    </Box>

                    {/* Demo toggle -- non-superuser broadcaster/adtech/agency/distributor; chameleons into ACME */}
                    <DemoToggle />

                    {/* Chameleon picker -- superuser + distributor, hidden when impersonation is active */}
                    <ChameleonPicker />

                    {/* Right side: theme, help, sign out */}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <ThemeToggle small={isMobile} />

                        <Tooltip title="Get help" arrow placement="bottom">
                            <IconButton
                                onClick={handleHelpClick}
                                size="medium"
                                sx={{
                                    color: colors.appBarText,
                                    opacity: 0.9,
                                    '&:hover': {
                                        backgroundColor: alpha(colors.appBarText, 0.08),
                                        opacity: 1,
                                    },
                                }}
                            >
                                <HelpOutlineOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Sign out" arrow placement="bottom">
                            <IconButton
                                onClick={logout}
                                size="medium"
                                sx={{
                                    color: colors.appBarText,
                                    opacity: 0.9,
                                    ml: 1,
                                    '&:hover': {
                                        backgroundColor: alpha(colors.appBarText, 0.08),
                                        opacity: 1,
                                    },
                                }}
                            >
                                <LogoutOutlinedIcon fontSize={isMobile ? 'small' : 'medium'} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Toolbar>
            </Container>
        </AppBar>
    );
}
